import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Form, Alert, Spinner, Table, Modal } from 'react-bootstrap';
import apiService from '../services/api';

const SETTING_CODE = 'reconciliation_report_types';
const SETTING_NAME = 'Reconciliation Report Types';

const emptyItem = {
    name: '',
    code: '',
    is_active: true,
    ordinal: 0,
    serial_column_number: 0,
    date_column_number: 0,
    headers: [],
    metadata_cells: [],   // [{key, value}]
    quality_fields: []    // [{name, column_number, threshold_value}]
};

const emptyMetadataRow = { key: '', value: '' };
const emptyQualityRow = { name: '', column_number: 0, threshold_value: 0 };

const normalizeMetadata = (raw) => {
    if (!Array.isArray(raw)) return [];
    return raw.map(entry => {
        if (entry && typeof entry === 'object') {
            const keys = Object.keys(entry);
            if (keys.length > 0) {
                return { key: keys[0], value: String(entry[keys[0]] ?? '') };
            }
        }
        return { key: '', value: '' };
    });
};

const metadataToWire = (rows) =>
    rows
        .filter(r => r.key && r.key.trim().length > 0)
        .map(r => ({ [r.key.trim()]: r.value }));

const ReconciliationReportTypesDefaults = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [settingData, setSettingData] = useState(null);
    const [items, setItems] = useState([]);

    const [showModal, setShowModal] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);
    const [itemForm, setItemForm] = useState(emptyItem);
    const [headersText, setHeadersText] = useState('');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await apiService.getOrganizationDefaultSettingByCode(SETTING_CODE);
            if (response.success && response.data) {
                setSettingData(response.data);
                const list = Array.isArray(response.data.value) ? response.data.value : [];
                setItems(list.map(it => ({
                    name: it.name || '',
                    code: it.code || '',
                    is_active: it.is_active !== undefined ? Boolean(it.is_active) : true,
                    ordinal: Number(it.ordinal) || 0,
                    serial_column_number: Number(it.serial_column_number) || 0,
                    date_column_number: Number(it.date_column_number) || 0,
                    headers: Array.isArray(it.headers) ? it.headers.map(h => String(h)) : [],
                    metadata_cells: normalizeMetadata(it.metadata_cells),
                    quality_fields: Array.isArray(it.quality_fields) ? it.quality_fields.map(q => ({
                        name: q.name || '',
                        column_number: Number(q.column_number) || 0,
                        threshold_value: Number(q.threshold_value) || 0
                    })) : []
                })));
            } else if (response.error && !String(response.error).toLowerCase().includes('not found')) {
                setError(response.error);
            }
        } catch (err) {
            console.error(`Error loading ${SETTING_CODE}:`, err);
            setError(`Failed to load ${SETTING_NAME}`);
        } finally {
            setLoading(false);
        }
    };

    const openAdd = () => {
        setEditingIndex(null);
        setItemForm({ ...emptyItem, headers: [], metadata_cells: [], quality_fields: [] });
        setHeadersText('');
        setShowModal(true);
    };

    const openEdit = (index) => {
        const it = items[index];
        setEditingIndex(index);
        setItemForm({
            name: it.name,
            code: it.code,
            is_active: Boolean(it.is_active),
            ordinal: Number(it.ordinal) || 0,
            serial_column_number: Number(it.serial_column_number) || 0,
            date_column_number: Number(it.date_column_number) || 0,
            headers: [...(it.headers || [])],
            metadata_cells: (it.metadata_cells || []).map(m => ({ ...m })),
            quality_fields: (it.quality_fields || []).map(q => ({ ...q }))
        });
        setHeadersText((it.headers || []).join('\n'));
        setShowModal(true);
    };

    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setItemForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // Metadata rows
    const addMetadataCellsRow = () => {
        setItemForm(prev => ({ ...prev, metadata_cells: [...prev.metadata_cells, { ...emptyMetadataRow }] }));
    };
    const updateMetadataCellsRow = (idx, field, value) => {
        setItemForm(prev => ({
            ...prev,
            metadata_cells: prev.metadata_cells.map((m, i) => i === idx ? { ...m, [field]: value } : m)
        }));
    };
    const deleteMetadataRow = (idx) => {
        setItemForm(prev => ({ ...prev, metadata_cells: prev.metadata_cells.filter((_, i) => i !== idx) }));
    };

    // Quality field rows
    const addQualityRow = () => {
        setItemForm(prev => ({ ...prev, quality_fields: [...prev.quality_fields, { ...emptyQualityRow }] }));
    };
    const updateQualityRow = (idx, field, value) => {
        setItemForm(prev => ({
            ...prev,
            quality_fields: prev.quality_fields.map((q, i) =>
                i === idx ? { ...q, [field]: field === 'name' ? value : (Number(value) || 0) } : q
            )
        }));
    };
    const deleteQualityRow = (idx) => {
        setItemForm(prev => ({ ...prev, quality_fields: prev.quality_fields.filter((_, i) => i !== idx) }));
    };

    const handleSaveItem = (e) => {
        e.preventDefault();
        const headers = headersText
            .split('\n')
            .map(h => h.trim())
            .filter(h => h.length > 0);

        const item = {
            name: (itemForm.name || '').trim(),
            code: (itemForm.code || '').trim(),
            is_active: Boolean(itemForm.is_active),
            ordinal: Number(itemForm.ordinal) || 0,
            metadata_cells: metadataToWire(itemForm.metadata_cells),
            headers,
            serial_column_number: Number(itemForm.serial_column_number) || 0,
            date_column_number: Number(itemForm.date_column_number) || 0,
            quality_fields: (itemForm.quality_fields || [])
                .filter(q => q.name && q.name.trim().length > 0)
                .map(q => ({
                    name: q.name.trim(),
                    column_number: Number(q.column_number) || 0,
                    threshold_value: Number(q.threshold_value) || 0
                }))
        };

        if (!item.name) return;

        // Store metadata back in normalized form for local state
        const localItem = {
            ...item,
            metadata_cells: normalizeMetadata(item.metadata_cells)
        };

        setItems(prev => {
            const list = [...prev];
            if (editingIndex === null) list.push(localItem);
            else list[editingIndex] = localItem;
            return list;
        });
        setShowModal(false);
    };

    const handleDeleteItem = (index) => {
        setItems(prev => prev.filter((_, i) => i !== index));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            const wireItems = items.map(it => ({
                name: it.name,
                code: it.code,
                is_active: Boolean(it.is_active),
                ordinal: Number(it.ordinal) || 0,
                metadata_cells: metadataToWire(it.metadata_cells),
                headers: it.headers || [],
                serial_column_number: Number(it.serial_column_number) || 0,
                date_column_number: Number(it.date_column_number) || 0,
                quality_fields: it.quality_fields || []
            }));

            const updateData = {
                name: SETTING_NAME,
                code: SETTING_CODE,
                value: wireItems
            };

            const response = settingData && settingData.id
                ? await apiService.updateOrganizationDefaultSetting(settingData.id, updateData)
                : await apiService.createOrganizationDefaultSetting(updateData);

            if (response.success) {
                setSuccess(`${SETTING_NAME} saved successfully!`);
                setSettingData(response.data);
            } else {
                setError(response.error || `Failed to save ${SETTING_NAME}`);
            }
        } catch (err) {
            console.error(`Error saving ${SETTING_CODE}:`, err);
            setError(`Failed to save ${SETTING_NAME}`);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center p-4">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
                <div className="mt-2">Loading {SETTING_NAME}...</div>
            </div>
        );
    }

    const sortedItems = [...items]
        .map((it, idx) => ({ ...it, _idx: idx }))
        .sort((a, b) => (a.ordinal - b.ordinal) || a.name.localeCompare(b.name));

    return (
        <div>
            <Row>
                <Col>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h2>
                            <i className="bi bi-file-earmark-spreadsheet me-2"></i>
                            {SETTING_NAME}
                        </h2>
                    </div>
                </Col>
            </Row>

            {error && (
                <Row className="mb-3"><Col>
                    <Alert variant="danger" dismissible onClose={() => setError('')}>
                        <i className="bi bi-exclamation-triangle me-2"></i>{error}
                    </Alert>
                </Col></Row>
            )}
            {success && (
                <Row className="mb-3"><Col>
                    <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                        <i className="bi bi-check-circle me-2"></i>{success}
                    </Alert>
                </Col></Row>
            )}

            <Form onSubmit={handleSave}>
                <Card className="mb-3">
                    <Card.Header className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">
                            <i className="bi bi-list-ul me-2"></i>
                            Report Types
                        </h5>
                        <Button variant="primary" size="sm" onClick={openAdd}>
                            <i className="bi bi-plus-lg me-1"></i> Add Report Type
                        </Button>
                    </Card.Header>
                    <Card.Body>
                        {sortedItems.length === 0 ? (
                            <div className="text-muted text-center py-3">No report types defined.</div>
                        ) : (
                            <Table striped hover responsive size="sm">
                                <thead>
                                    <tr>
                                        <th>Ordinal</th>
                                        <th>Name</th>
                                        <th>Code</th>
                                        <th>Headers</th>
                                        <th>Quality Fields</th>
                                        <th>Active</th>
                                        <th className="text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedItems.map(it => (
                                        <tr key={it._idx}>
                                            <td>{it.ordinal}</td>
                                            <td>{it.name}</td>
                                            <td><code>{it.code}</code></td>
                                            <td>{(it.headers || []).length}</td>
                                            <td>{(it.quality_fields || []).length}</td>
                                            <td>
                                                {it.is_active
                                                    ? <span className="badge bg-success">Yes</span>
                                                    : <span className="badge bg-secondary">No</span>}
                                            </td>
                                            <td className="text-end">
                                                <Button size="sm" variant="outline-primary" className="me-2"
                                                    onClick={() => openEdit(it._idx)}>
                                                    <i className="bi bi-pencil"></i>
                                                </Button>
                                                <Button size="sm" variant="outline-danger"
                                                    onClick={() => handleDeleteItem(it._idx)}>
                                                    <i className="bi bi-trash"></i>
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </Card.Body>
                </Card>

                <div className="d-flex justify-content-end">
                    <Button type="submit" variant="success" disabled={saving}>
                        {saving ? (<><Spinner size="sm" animation="border" className="me-2" />Saving...</>)
                            : (<><i className="bi bi-save me-2"></i>Save Changes</>)}
                    </Button>
                </div>
            </Form>

            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{editingIndex === null ? 'Add' : 'Edit'} Reconciliation Report Type</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSaveItem}>
                    <Modal.Body>
                        <Row>
                            <Col md={8}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Name</Form.Label>
                                    <Form.Control type="text" name="name" value={itemForm.name}
                                        onChange={handleFormChange} required />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Ordinal</Form.Label>
                                    <Form.Control type="number" min="0" step="1" name="ordinal"
                                        value={itemForm.ordinal} onChange={handleFormChange} />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mb-3">
                            <Form.Label>Code</Form.Label>
                            <Form.Control type="text" name="code" value={itemForm.code}
                                onChange={handleFormChange} placeholder="e.g. _DAILY_LINES_ACTIVATIONS_REPORT_" />
                        </Form.Group>
                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Serial Column #</Form.Label>
                                    <Form.Control type="number" min="0" step="1" name="serial_column_number"
                                        value={itemForm.serial_column_number} onChange={handleFormChange} />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Date Column #</Form.Label>
                                    <Form.Control type="number" min="0" step="1" name="date_column_number"
                                        value={itemForm.date_column_number} onChange={handleFormChange} />
                                </Form.Group>
                            </Col>
                            <Col md={4} className="d-flex align-items-center">
                                <Form.Check type="checkbox" name="is_active" id="reconReportActive"
                                    label="Active" checked={!!itemForm.is_active} onChange={handleFormChange} />
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>Headers (one per line)</Form.Label>
                            <Form.Control as="textarea" rows={6}
                                value={headersText}
                                onChange={(e) => setHeadersText(e.target.value)}
                                placeholder={'TM Date\nID Date\nSim Serial Number\n...'} />
                            <Form.Text className="text-muted">
                                Each line represents one column header.
                            </Form.Text>
                        </Form.Group>

                        <Card className="mb-3">
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <strong>Metadata Cells</strong>
                                <Button size="sm" variant="outline-primary" onClick={addMetadataCellsRow}>
                                    <i className="bi bi-plus-lg me-1"></i> Add
                                </Button>
                            </Card.Header>
                            <Card.Body>
                                {itemForm.metadata_cells.length === 0 ? (
                                    <div className="text-muted small">No metadata entries.</div>
                                ) : (
                                    <Table size="sm" className="mb-0">
                                        <thead>
                                            <tr>
                                                <th>Key</th>
                                                <th>Value</th>
                                                <th style={{ width: 60 }}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {itemForm.metadata_cells.map((m, idx) => (
                                                <tr key={idx}>
                                                    <td>
                                                        <Form.Control size="sm" type="text" value={m.key}
                                                            onChange={(e) => updateMetadataCellsRow(idx, 'key', e.target.value)}
                                                            placeholder="e.g. total_activations" />
                                                    </td>
                                                    <td>
                                                        <Form.Control size="sm" type="text" value={m.value}
                                                            onChange={(e) => updateMetadataCellsRow(idx, 'value', e.target.value)}
                                                            placeholder="e.g. B6" />
                                                    </td>
                                                    <td className="text-end">
                                                        <Button size="sm" variant="outline-danger"
                                                            onClick={() => deleteMetadataRow(idx)}>
                                                            <i className="bi bi-trash"></i>
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                )}
                            </Card.Body>
                        </Card>

                        <Card className="mb-2">
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <strong>Quality Fields</strong>
                                <Button size="sm" variant="outline-primary" onClick={addQualityRow}>
                                    <i className="bi bi-plus-lg me-1"></i> Add
                                </Button>
                            </Card.Header>
                            <Card.Body>
                                {itemForm.quality_fields.length === 0 ? (
                                    <div className="text-muted small">No quality fields defined.</div>
                                ) : (
                                    <Table size="sm" className="mb-0">
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Column #</th>
                                                <th>Threshold</th>
                                                <th style={{ width: 60 }}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {itemForm.quality_fields.map((q, idx) => (
                                                <tr key={idx}>
                                                    <td>
                                                        <Form.Control size="sm" type="text" value={q.name}
                                                            onChange={(e) => updateQualityRow(idx, 'name', e.target.value)}
                                                            placeholder="e.g. top_up_amount" />
                                                    </td>
                                                    <td>
                                                        <Form.Control size="sm" type="number" min="0" step="1"
                                                            value={q.column_number}
                                                            onChange={(e) => updateQualityRow(idx, 'column_number', e.target.value)} />
                                                    </td>
                                                    <td>
                                                        <Form.Control size="sm" type="number" min="0" step="1"
                                                            value={q.threshold_value}
                                                            onChange={(e) => updateQualityRow(idx, 'threshold_value', e.target.value)} />
                                                    </td>
                                                    <td className="text-end">
                                                        <Button size="sm" variant="outline-danger"
                                                            onClick={() => deleteQualityRow(idx)}>
                                                            <i className="bi bi-trash"></i>
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                )}
                            </Card.Body>
                        </Card>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
                        <Button type="submit" variant="primary">Save</Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

export default ReconciliationReportTypesDefaults;
